import React, { useEffect, useState } from 'react';
import useShippingFee from '../../hooks/useShippingFee';

// Helper to get flag URL from country name
function getFlagUrl(country) {
  // Try to get ISO 2-letter code from country name (simple mapping for demo)
  const countryMap = {
    'Tunisia': 'tn',
    'France': 'fr',
    'Germany': 'de',
    'Italy': 'it',
    'Spain': 'es',
    'United States': 'us',
    'USA': 'us',
    'United Kingdom': 'gb',
    'UK': 'gb',
    'Canada': 'ca',
    'Belgium': 'be',
    'Netherlands': 'nl',
    'Switzerland': 'ch',
    'Algeria': 'dz',
    'Morocco': 'ma',
    'Libya': 'ly',
    'Turkey': 'tr',
    'Saudi Arabia': 'sa',
    'Qatar': 'qa',
    'UAE': 'ae',
    'United Arab Emirates': 'ae',
    'Portugal': 'pt',
    'Greece': 'gr',
    'Egypt': 'eg',
    'Jordan': 'jo',
    'Lebanon': 'lb',
    'Syria': 'sy',
    'Iraq': 'iq',
    'Palestine': 'ps',
    'Israel': 'il',
    'Austria': 'at',
    'Poland': 'pl',
    'Czech Republic': 'cz',
    'Slovakia': 'sk',
    'Romania': 'ro',
    'Bulgaria': 'bg',
    'Hungary': 'hu',
    'Denmark': 'dk',
    'Sweden': 'se',
    'Norway': 'no',
    'Finland': 'fi',
    'Ireland': 'ie',
    'Russia': 'ru',
    'China': 'cn',
    'Japan': 'jp',
    'South Korea': 'kr',
    'India': 'in',
    'Australia': 'au',
    'New Zealand': 'nz',
    // Add more as needed
  };
  const code = countryMap[country] || '';
  return code ? `https://flagcdn.com/24x18/${code}.png` : null;
}

const AdminShipping = () => {
  const [newCountry, setNewCountry] = useState('');
  const [newFee, setNewFee] = useState('');
  const [newCurrency, setNewCurrency] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [editCountry, setEditCountry] = useState('');
  const [editFee, setEditFee] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const { allFees, fetchAllFees, addFee, updateFee, deleteFee, loading, error } = useShippingFee();

  useEffect(() => {
    fetchAllFees();
  }, [fetchAllFees]);

  const handleDelete = (id) => {
    deleteFee(id);
  };

  const handleEdit = (idx) => {
    setEditIndex(idx);
    setEditCountry(allFees[idx].country);
    setEditFee(allFees[idx].fee);
    setEditCurrency(allFees[idx].currency || '');
  };

  const handleUpdate = (id) => {
    updateFee(id, editCountry, editFee, editCurrency);
    setEditIndex(null);
  };

  const handleAdd = () => {
    if (newCountry && newFee) {
      addFee(newCountry, newFee, newCurrency);
      setNewCountry('');
      setNewFee('');
      setNewCurrency('');
    }
  };

  return (
    <div className="p-2 sm:p-4 md:p-8 w-full flex flex-col items-center">
      <div className="w-full max-w-xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold mb-4">Shipping Fees</h2>
        <div className="flex flex-col gap-2 mb-6 w-full">
          <input
            className="border rounded px-2 py-2 text-sm sm:text-base md:text-lg w-full"
            value={newCountry}
            onChange={e => setNewCountry(e.target.value)}
            placeholder="Country"
          />
          <input
            className="border rounded px-2 py-2 text-sm sm:text-base md:text-lg w-full"
            value={newFee}
            onChange={e => setNewFee(e.target.value)}
            placeholder="e.g. 8"
          />
          <input
            className="border rounded px-2 py-2 text-sm sm:text-base md:text-lg w-full"
            value={newCurrency}
            onChange={e => setNewCurrency(e.target.value)}
            placeholder="Currency (e.g. TND, €)"
          />
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow transition-colors text-sm sm:text-base md:text-lg w-full" onClick={handleAdd}>+ Add Shipping Fee</button>
        </div>
        {loading && <div>Loading...</div>}
        {error && <div className="text-red-500">{error}</div>}
        <div className="w-full">
          <table className="w-full bg-white rounded-xl shadow-lg text-xs sm:text-sm md:text-sm">
            <thead className="bg-blue-100">
              <tr>
                <th className="px-2 sm:px-4 py-2 border text-center w-1/4">Country</th>
                <th className="px-2 sm:px-4 py-2 border text-center w-1/4">Shipping Fee</th>
                <th className="px-2 sm:px-4 py-2 border text-center w-1/4">Currency</th>
                <th className="px-2 sm:px-4 py-2 border text-center w-1/4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allFees.map((row, idx) => (
                <tr key={row._id || idx}>
                  <td className="px-2 sm:px-4 py-2 border text-center align-middle">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {(() => {
                        const flagUrl = getFlagUrl(editIndex === idx ? editCountry : row.country);
                        if (flagUrl) {
                          return <img src={flagUrl} alt="flag" className="inline w-5 h-4 rounded-sm border" />;
                        } else {
                          return <span className="mr-2">🌐</span>; // fallback emoji
                        }
                      })()}
                      {editIndex === idx ? (
                        <input
                          className="border rounded px-2 py-1 text-xs sm:text-base md:text-lg"
                          value={editCountry}
                          onChange={e => setEditCountry(e.target.value)}
                        />
                      ) : (
                        row.country
                      )}
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-2 border text-center align-middle">
                    {editIndex === idx ? (
                      <input
                        className="border rounded px-2 py-1 text-xs sm:text-base md:text-lg"
                        value={editFee}
                        onChange={e => setEditFee(e.target.value)}
                      />
                    ) : (
                      row.fee
                    )}
                  </td>
                  <td className="px-2 sm:px-4 py-2 border text-center align-middle">
                    {editIndex === idx ? (
                      <input
                        className="border rounded px-2 py-1 text-xs sm:text-base md:text-lg"
                        value={editCurrency}
                        onChange={e => setEditCurrency(e.target.value)}
                      />
                    ) : (
                      row.currency || ''
                    )}
                  </td>
                  <td className="px-2 sm:px-4 py-2 border align-middle">
                    <div className="flex flex-col sm:flex-row md:flex-row lg:flex-row justify-center items-center gap-2 flex-wrap">
                      {editIndex === idx ? (
                        <>
                          <button className="bg-green-500 text-white px-3 py-1 rounded text-xs sm:text-base md:text-lg mb-1 sm:mb-0" onClick={() => handleUpdate(row._id)}>Save</button>
                          <button className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-xs sm:text-base md:text-lg" onClick={() => setEditIndex(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs sm:text-base md:text-lg mb-1 sm:mb-0" onClick={() => handleEdit(idx)}>Edit</button>
                          <button className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs sm:text-base md:text-lg" onClick={() => handleDelete(row._id)}>Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminShipping; 